
import { useState, useEffect } from 'react';
import { useEmailBuilderStore } from '@/store/emailBuilderStore';
import { emailBuilderService } from '@/lib/api-services/email-builder-service';
interface PreviewModalProps {
  onClose: () => void;
}

type DeviceType = 'desktop' | 'mobile';

export function PreviewModal({ onClose }: PreviewModalProps) {
  const { document, blockTree } = useEmailBuilderStore();
  const [device, setDevice] = useState<DeviceType>('desktop');
  const [preview, setPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!document) return;

    const abortController = new AbortController();

    // Use Promise.resolve() instead of setTimeout for better cleanup
    Promise.resolve().then(async () => {
      if (abortController.signal.aborted) return;
      setIsLoading(true);

      try {
        const result = await emailBuilderService.previewDocument(document.id);
        if (!abortController.signal.aborted) setPreview(result.html);
      } catch (error) {
        console.error('Preview rendering error:', error);
      }

      if (!abortController.signal.aborted) setIsLoading(false);
    });

    return () => {
      abortController.abort();
    };
  }, [document, blockTree]);

  const deviceWidth = device === 'desktop' ? '100%' : '375px';
  const deviceHeight = device === 'desktop' ? '100%' : '667px';

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    // Prevent background scrolling when modal is open
    window.document.body.style.overflow = 'hidden';
    return () => {
      window.document.body.style.overflow = '';
    };
  }, []);

  return (
    <div className="lw-preview-modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => onClose()}>
      <div className="lw-preview-modal flex h-[95vh] w-[60vw] flex-col rounded-lg bg-card shadow-xl" onClick={(e) => e.stopPropagation()}
      style={{width: device === 'desktop' ? '60vw' : 'fit-content' }}>
        {/* Header */}
        <div className="lw-preview-modal-header flex items-center justify-between border-b border-border px-6 py-3">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-foreground">Email Preview</h2>
            
            {/* Device Switcher */}
            <div className="lw-preview-modal-device-switcher flex items-center gap-1 rounded-md border border-border bg-background p-1">
              <button
                onClick={() => setDevice('desktop')}
                className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                  device === 'desktop'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <svg className="h-4 w-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Desktop
              </button>
              <button
                onClick={() => setDevice('mobile')}
                className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                  device === 'mobile'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <svg className="h-4 w-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Mobile
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Close Button */}
            <button
              onClick={onClose}
              className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              aria-label="Close preview"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="lw-preview-modal-content flex-1 overflow-auto bg-muted p-4" style={{height: '100%'}}>
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent" />
                <p className="text-sm text-muted-foreground">Rendering preview...</p>
              </div>
            </div>
          ) : preview ? (
            <div className="flex justify-center" style={{height: '100%'}}>
                <div
                  className="transition-all duration-300 ease-in-out"
                  style={{
                    height: '100%',
                    width: deviceWidth,
                    maxWidth: device === 'desktop' ? '100%' : '375px',
                  }}
                >
                  <div
                    className={`mx-auto overflow-auto rounded-lg bg-white shadow-2xl ${
                      device === 'mobile' ? 'border-8 border-gray-800' : ''
                    }`}
                    style={{
                      height: device === 'mobile' ? deviceHeight : '100%',
                      maxHeight: '80vh',
                    }}
                  >
                    <iframe
                      srcDoc={preview}
                      className="h-full w-full border-0"
                      title="Email preview"
                      sandbox="allow-same-origin"
                    />
                  </div>

                  {/* Device label */}
                  <div className="mt-4 text-center text-sm text-muted-foreground">
                    {device === 'desktop' ? '' : 'iPhone 8 (375×667)'}
                  </div>
                </div>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-sm text-muted-foreground">No preview available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="lw-preview-modal-footer flex items-center justify-between border-t border-border px-6 py-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
